/**
 * Print geometry (implementation-plan.md §5).
 *
 * A band always occupies exactly the usable page width, whatever its step: the
 * pixel scale is derived from the page, never from the screen's `pxPerTick`.
 * That is what makes Mode A's per-band scales land on paper consistently.
 */

import type { LayoutOptions, TimelineLayout } from './layout';
import { PRINT_METRICS } from './metrics';

const PX_PER_MM = 96 / 25.4;

/** A4, short and long edge. Which is which depends on the orientation. */
const A4_MM = { short: 210, long: 297 };

/** The legibility floor from §5.1: below this, a four-digit label is unreadable. */
export const MIN_TICK_PX = 15;

export interface PrintSettings {
  /**
   * Landscape reads better — wider bands, fewer graduations sacrificed — but
   * portrait offers 285 mm of height against 190, which is the axis that runs
   * out first on a dense chart.
   */
  orientation: 'landscape' | 'portrait';
}

export const DEFAULT_PRINT_SETTINGS: PrintSettings = { orientation: 'landscape' };

export interface PageGeometry {
  widthMm: number;
  heightMm: number;
  marginBlockMm: number;
  marginInlineMm: number;
  /** What a band body gets across the page — the same for every band, by design. */
  usableWidth: number;
  usableHeight: number;
}

export function pageGeometry(settings: PrintSettings): PageGeometry {
  const landscape = settings.orientation === 'landscape';
  const widthMm = landscape ? A4_MM.long : A4_MM.short;
  const heightMm = landscape ? A4_MM.short : A4_MM.long;

  // As close to the printers' unprintable edge as is safe.
  const marginBlockMm = 6;
  const marginInlineMm = 8;

  return {
    widthMm,
    heightMm,
    marginBlockMm,
    marginInlineMm,
    usableWidth: (widthMm - 2 * marginInlineMm) * PX_PER_MM,
    usableHeight: (heightMm - 2 * marginBlockMm) * PX_PER_MM,
  };
}

/** What a band actually gets to draw in: the page, less the band-name gutter. */
export function bandWidthFor(settings: PrintSettings): number {
  return pageGeometry(settings).usableWidth - PRINT_METRICS.gutterW;
}

/** How many graduations a band can carry legibly at this page width. */
export function tickBudget(settings: PrintSettings): number {
  return Math.floor(bandWidthFor(settings) / MIN_TICK_PX);
}

export function printLayoutOptions(
  settings: PrintSettings = DEFAULT_PRINT_SETTINGS,
  extra: Partial<LayoutOptions> = {},
): LayoutOptions {
  // The gutter holding the band names is taken off the page before the bands
  // are scaled, so a band still ends exactly where the printable area does.
  const width = bandWidthFor(settings);
  return {
    ...extra,
    metrics: PRINT_METRICS,
    bandWidth: () => width,
  };
}

/**
 * Non-blocking print warnings: the chart still works on screen, and only the
 * user can decide how to thin it out.
 */
export function printWarnings(
  layout: TimelineLayout,
  settings: PrintSettings = DEFAULT_PRINT_SETTINGS,
): string[] {
  const { usableHeight } = pageGeometry(settings);
  const budget = tickBudget(settings);
  const warnings: string[] = [];

  for (const band of layout.bands) {
    if (band.height > usableHeight) {
      warnings.push(
        `Période « ${band.band.label} » : ${Math.round(band.height)} px de haut pour ` +
          `${Math.round(usableHeight)} px utiles sur une page. Elle débordera à l'impression.`,
      );
    }

    // The hard cap in validate.ts is set for landscape; portrait is narrower, so
    // a band that is legal on screen can still be too dense on paper.
    if (band.ticks.length > budget) {
      const orientation = settings.orientation === 'portrait' ? 'portrait' : 'paysage';
      warnings.push(
        `Période « ${band.band.label} » : ${band.ticks.length} graduations pour ${budget} ` +
          `optimales en ${orientation}. Augmenter le pas pour une meilleure mise en page ` +
          `d'impression.`,
      );
    }
  }

  return warnings;
}
